import React, { useEffect, useState, useRef } from 'react';
import { useTranslation, useTranslationContext } from '../../i18n/TranslationProvider';
import logoWithOutClaim from '../../logo/logo-sbk.svg';
import useOverrideComponentStyles from '../../helper/useOverrideComponentStyles';
import sanitizeBackendHtml from '../../helper/sanitizeBackendHtml';

const languageLabels = {
    de: 'Deutsch',
    en: 'English',
};

const languageContactLabels = {
    de: 'Contact',
    en: 'Kontakt',
};

const headerLinkStyles = `
    div.link-wrapper:not(#dummy) {
        height: 100%
    }
`;

const getOtherLanguage = (availableLocales, locale) => {
    // We only switch between the first two languages right now, but the ATM might support more languages in the future
    // so we have to account for that
    const currentLocaleIndex = availableLocales.indexOf(locale);
    const otherLanguages = [
        ...availableLocales.slice(0, currentLocaleIndex),
        ...availableLocales.slice(currentLocaleIndex + 1),
    ];

    return otherLanguages[0];
};

const LanguageSwitch = () => {
    const { availableLocales, locale, setLocale } = useTranslationContext();
    const ref = useOverrideComponentStyles(headerLinkStyles);

    if (availableLocales.length <= 1) {
        return null;
    }

    const otherLanguage = getOtherLanguage(availableLocales, locale);

    return (
        <sbk-link
            ref={ref}
            icon="language"
            variant="reduced-navigation"
            href={`/${otherLanguage}/`}
            onClick={(e) => {
                e.preventDefault();
                setLocale(otherLanguage);
            }}
            size="2xs"
            is-bold=""
        >
            {languageLabels[otherLanguage] || otherLanguage.toUpperCase()}
        </sbk-link>
    );
};

const Header = ({ campaign, logoLink = undefined }) => {
    const { availableLocales, locale } = useTranslationContext();
    const otherLanguage = getOtherLanguage(availableLocales, locale);
    const [isMobile, setIsMobile] = useState(false);
    const ref = useOverrideComponentStyles(headerLinkStyles);
    const contactLabel = useTranslation('contact');
    const buttonRef = useRef(null);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 768px)');
        const handleMediaQueryChange = (event) => {
            setIsMobile(event.matches);
        };

        // Set initial value
        setIsMobile(mediaQuery.matches);

        // Add listener
        mediaQuery.addEventListener('change', handleMediaQueryChange);

        // Clean up listener on unmount
        return () => {
            mediaQuery.removeEventListener('change', handleMediaQueryChange);
        };
    }, []);

    const handleLinkClick = (e) => {
        e.preventDefault();
        if (buttonRef.current) {
            buttonRef.current.click();
        }
    };

    return (
        <>
            <sbk-meta-nav
                variant="reduced"
                logo-path={logoWithOutClaim}
                logo-alt-text="Logo der SBK"
                logo-link={logoLink}
            >
                <sbk-meta-nav-items slot="cta">
                    {isMobile && campaign?.organizer?.signature && (
                        <sbk-main-nav-item>
                            <sbk-link
                                ref={ref}
                                icon="chat"
                                variant="reduced-navigation"
                                href="#"
                                size="2xs"
                                is-bold=""
                                onClick={handleLinkClick}
                            >
                                {languageContactLabels[otherLanguage] || languageContactLabels.en}
                            </sbk-link>
                        </sbk-main-nav-item>
                    )}
                    <sbk-meta-nav-item>
                        <LanguageSwitch />
                    </sbk-meta-nav-item>
                </sbk-meta-nav-items>
            </sbk-meta-nav>
            {isMobile && campaign?.organizer?.signature && (
                <sbk-modal heading={contactLabel}>
                    <sbk-button slot="triggerElement" ref={buttonRef} class="hidden" />
                    <div
                        className="backend-content"
                        dangerouslySetInnerHTML={{ __html: sanitizeBackendHtml(campaign.organizer.signature) }}
                    />
                </sbk-modal>
            )}
        </>
    );
};

export default Header;
